// useAgent — SSE streaming hook for agent chat
// Manages chat state machine: idle → working → idle/error

import { useState, useCallback, useRef, useEffect, useSyncExternalStore } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  hasActiveAgentStream,
  getActiveAgentStreamThreadId,
  registerAgentStream,
  releaseAgentStream,
  subscribeToAgentStreamLifecycle,
  stopAgentStream,
} from "../lib/agent-stream-lifecycle";
import { streamSSE, type AgentStreamErrorState } from "../lib/sse";
import type {
  AgentSessionStatus,
  AgentToolCall,
  AgentArtifact,
  AgentThreadListItem,
  BrandReadinessMissingKey,
  ChatMessage,
} from "../../../shared/types.ts";

// ── Types ──

type ChatState = "idle" | "working" | "error";

interface AgentMessage extends ChatMessage {
  tool_calls?: AgentToolCall[];
  artifacts?: AgentArtifact[];
}

export interface SynthesisLearnedBanner {
  execution_id: string;
  summary: string;
  ok: boolean;
}

export type AgentUiError = AgentStreamErrorState;

interface UseAgentReturn {
  messages: AgentMessage[];
  streamingText: string;
  activeTool: AgentToolCall | null;
  /** PRD-0032 Phase 7 — tool_progress SSE rows for this turn */
  toolProgress: Array<{ id: string; name: string; message: string }>;
  activityStatus: "preparing" | "working" | "retrying" | null;
  /** PRD-0032 Phase 7 — last synthesis_learned event for this turn */
  synthesisLearned: SynthesisLearnedBanner | null;
  isStreaming: boolean;
  /** Another thread in this brand owns the one active workspace stream. */
  isBrandStreamBusy: boolean;
  completedTurnCount: number;
  lastCompletedThreadId: string | null;
  chatState: ChatState;
  sessionStatus: AgentSessionStatus | "idle";
  isStopping: boolean;
  error: AgentUiError | null;
  brandNotReadyMissing: BrandReadinessMissingKey[] | null;
  clearError: () => void;
  dismissBrandNotReady: () => void;
  sendMessage: (content: string) => boolean;
  retryLastMessage: () => boolean;
  stopSession: () => Promise<void>;
}

// ── useAgent Hook ──

export function useAgent(threadId: string | null, brandId: string | null = null): UseAgentReturn {
  const queryClient = useQueryClient();
  const [streamingText, setStreamingText] = useState("");
  const [activeTool, setActiveTool] = useState<AgentToolCall | null>(null);
  const [toolProgress, setToolProgress] = useState<Array<{ id: string; name: string; message: string }>>([]);
  const [activityStatus, setActivityStatus] = useState<"preparing" | "working" | "retrying" | null>(null);
  const [synthesisLearned, setSynthesisLearned] = useState<SynthesisLearnedBanner | null>(null);
  const [chatState, setChatState] = useState<ChatState>("idle");
  const [sessionStatus, setSessionStatus] = useState<AgentSessionStatus | "idle">("idle");
  const [isStopping, setIsStopping] = useState(false);
  const [completedTurnCount, setCompletedTurnCount] = useState(0);
  const [lastCompletedThreadId, setLastCompletedThreadId] = useState<string | null>(null);
  const [error, setError] = useState<AgentUiError | null>(null);
  const [brandNotReadyMissing, setBrandNotReadyMissing] = useState<BrandReadinessMissingKey[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeStreamTokenRef = useRef(0);
  const lastSubmittedMessageRef = useRef<string | null>(null);
  /** Synchronous guard — React state can lag one frame; blocks double-submit / double Enter. */
  const sendInFlightRef = useRef(false);
  const activeStreamThreadId = useSyncExternalStore(
    subscribeToAgentStreamLifecycle,
    () => getActiveAgentStreamThreadId(brandId),
    () => null,
  );
  const isBrandStreamBusy = activeStreamThreadId !== null && activeStreamThreadId !== threadId;
  const previouslyObservedMatchingStreamRef = useRef(false);

  const clearError = useCallback(() => {
    setError(null);
    setChatState((s) => (s === "error" ? "idle" : s));
    setSessionStatus((current) => (current === "failed" ? "idle" : current));
  }, []);

  useEffect(() => {
    sendInFlightRef.current = false;
    setError(null);
    setStreamingText("");
    setActiveTool(null);
    setToolProgress([]);
    setActivityStatus(null);
    setSynthesisLearned(null);
    const isBackgroundStream = activeStreamThreadId === threadId;
    setChatState(isBackgroundStream ? "working" : "idle");
    setSessionStatus(isBackgroundStream ? "running" : "idle");
    setIsStopping(false);
    setCompletedTurnCount(0);
    setLastCompletedThreadId(null);
    setBrandNotReadyMissing(null);
    activeStreamTokenRef.current += 1;

    return () => {
      // The tab can remain open while a sibling workspace route is active.
      // Detach this page's callbacks but leave the registered stream running;
      // WorkspaceTabBar stops it only when the chat tab itself is closed.
      activeStreamTokenRef.current += 1;
      abortRef.current = null;
      sendInFlightRef.current = false;
    };
  }, [threadId, brandId]);

  // The SSE callbacks on the backgrounded page are intentionally detached. The
  // lifecycle store is the remount-safe source for its liveness: only the same
  // thread can resume a working state, and that observed stream becoming absent
  // returns this thread to an idle/completed state. A different selected thread
  // never inherits or cancels the brand's active stream.
  useEffect(() => {
    const matchingStream = activeStreamThreadId === threadId && threadId !== null;
    if (matchingStream) {
      previouslyObservedMatchingStreamRef.current = true;
      setChatState("working");
      setSessionStatus("running");
      return;
    }
    if (previouslyObservedMatchingStreamRef.current && activeStreamThreadId === null && sessionStatus === "running") {
      previouslyObservedMatchingStreamRef.current = false;
      setChatState("idle");
      setSessionStatus("completed");
      setCompletedTurnCount((count) => count + 1);
      setLastCompletedThreadId(threadId);
      queryClient.invalidateQueries({ queryKey: ["agent-thread", threadId] });
      queryClient.invalidateQueries({ queryKey: ["agent-threads"] });
    }
  }, [activeStreamThreadId, threadId, sessionStatus, queryClient]);

  // Fetch thread messages — API wraps in { data: { thread, messages } }
  const { data: threadData } = useQuery({
    queryKey: ["agent-thread", threadId],
    queryFn: () => api.get<{ data: { thread: unknown; messages: AgentMessage[] } }>(`/api/agent/threads/${threadId}`),
    enabled: !!threadId,
  });

  const messages = threadData?.data?.messages ?? [];

  const startMessageStream = useCallback(
    (content: string) => {
      if (!threadId || !brandId || sendInFlightRef.current || hasActiveAgentStream(brandId)) return false;

      sendInFlightRef.current = true;
      activeStreamTokenRef.current += 1;
      const streamToken = activeStreamTokenRef.current;
      lastSubmittedMessageRef.current = content;
      setChatState("working");
      setSessionStatus("running");
      setIsStopping(false);
      setStreamingText("");
      setActiveTool(null);
      setToolProgress([]);
      setActivityStatus("preparing");
      setSynthesisLearned(null);
      setError(null);
      setBrandNotReadyMissing(null);

      const controller = new AbortController();
      abortRef.current = controller;
      registerAgentStream(brandId, threadId, controller);

      // Optimistically add user message to the list
      queryClient.setQueryData(
        ["agent-thread", threadId],
        (old: { data: { thread: unknown; messages: AgentMessage[] } } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              messages: [
                ...old.data.messages,
                {
                  id: `temp-${Date.now()}`,
                  thread_id: threadId,
                  role: "user" as const,
                  content,
                  agent_name: null,
                  created_at: new Date().toISOString(),
                },
              ],
            },
          };
        },
      );

      let accumulated = "";

      void (async () => {
        try {
          await streamSSE(
            `/api/agent/threads/${threadId}/messages`,
            { content },
            {
              onDelta(text) {
                if (streamToken !== activeStreamTokenRef.current) return;
                accumulated += text;
                setStreamingText(accumulated);
              },
              onToolStart(id, name, input) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setActivityStatus("working");
                setActiveTool({ id, name, input, status: "running", output: null, duration_ms: null });
              },
              onToolEnd(id, name, status, output) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setActiveTool((prev) =>
                  prev?.id === id ? { ...prev, status, output: output ?? null, duration_ms: null } : prev,
                );
              },
              onToolProgress(id, name, message) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setToolProgress((prev) => [...prev, { id, name, message }]);
              },
              onSynthesisLearned(executionId, summary, ok) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setSynthesisLearned({ execution_id: executionId, summary, ok });
              },
              onStatus(status, detail) {
                if (streamToken !== activeStreamTokenRef.current || status !== "thinking") return;
                setActivityStatus(detail?.includes("Retrying with another execution path") ? "retrying"
                  : detail === "Preparing response..." ? "preparing" : "working");
              },
              onDone() {
                if (streamToken !== activeStreamTokenRef.current) return;
                setChatState("idle");
                setSessionStatus("completed");
                setIsStopping(false);
                setStreamingText("");
                setActiveTool(null);
                setToolProgress([]);
                setActivityStatus(null);
                setCompletedTurnCount((count) => count + 1);
                setLastCompletedThreadId(threadId);
                abortRef.current = null;
                releaseAgentStream(brandId, controller);
                queryClient.invalidateQueries({ queryKey: ["agent-thread", threadId] });
                queryClient.invalidateQueries({ queryKey: ["agent-threads"] });
              },
              onBrandNotReady(missing) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setBrandNotReadyMissing(missing);
              },
              onError(nextError) {
                if (streamToken !== activeStreamTokenRef.current) return;
                setChatState("error");
                setSessionStatus("failed");
                setIsStopping(false);
                setError(nextError);
                setStreamingText("");
                setActiveTool(null);
                setToolProgress([]);
                setActivityStatus(null);
                setSynthesisLearned(null);
                abortRef.current = null;
                releaseAgentStream(brandId, controller);
                queryClient.invalidateQueries({ queryKey: ["agent-thread", threadId] });
                queryClient.invalidateQueries({ queryKey: ["agent-threads"] });
              },
            },
            controller.signal,
          );
        } finally {
          releaseAgentStream(brandId, controller);
          if (streamToken === activeStreamTokenRef.current) {
            sendInFlightRef.current = false;
          }
        }
      })();

      return true;
    },
    [threadId, brandId, queryClient],
  );

  const retryLastMessage = useCallback(() => {
    if (!lastSubmittedMessageRef.current) return false;
    return startMessageStream(lastSubmittedMessageRef.current);
  }, [startMessageStream]);

  const stopSession = useCallback(async () => {
    // A brand can retain an older stream while the operator views a different
    // thread. The visible Stop control must not cancel that other thread.
    const isCurrentThreadStream = getActiveAgentStreamThreadId(brandId) === threadId;
    if (!threadId || !brandId || !isCurrentThreadStream || isStopping || (sessionStatus !== "running" && !hasActiveAgentStream(brandId))) {
      return;
    }

    setIsStopping(true);
    activeStreamTokenRef.current += 1;
    sendInFlightRef.current = false;
    abortRef.current = null;

    try {
      await stopAgentStream(brandId);
      setChatState("idle");
      setSessionStatus("cancelled");
      setError(null);
      setStreamingText("");
      setActiveTool(null);
      setToolProgress([]);
      setActivityStatus(null);
      setSynthesisLearned(null);
      queryClient.invalidateQueries({ queryKey: ["agent-thread", threadId] });
      queryClient.invalidateQueries({ queryKey: ["agent-threads"] });
    } catch (err) {
      setChatState("error");
      setSessionStatus("failed");
      setError({
        code: "unknown",
        message: err instanceof Error ? err.message : "Failed to stop the active session.",
        retryable: true,
      });
    } finally {
      setIsStopping(false);
    }
  }, [isStopping, queryClient, sessionStatus, threadId, brandId]);

  return {
    messages,
    streamingText,
    activeTool,
    toolProgress,
    activityStatus,
    synthesisLearned,
    isStreaming: chatState === "working",
    isBrandStreamBusy,
    completedTurnCount,
    lastCompletedThreadId,
    chatState,
    sessionStatus,
    isStopping,
    error,
    brandNotReadyMissing,
    clearError,
    dismissBrandNotReady: () => setBrandNotReadyMissing(null),
    sendMessage: startMessageStream,
    retryLastMessage,
    stopSession,
  };
}

// ── useAgentThreads Hook ──

export function useAgentThreads(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data, isLoading, status, refetch } = useQuery({
    queryKey: ["agent-threads", brandId],
    queryFn: () => api.get<{ data: AgentThreadListItem[] }>(`/api/agent/threads?brand_id=${brandId}`),
    enabled: !!brandId,
  });

  const createThread = useMutation({
    mutationFn: async (brandId: string) => {
      const res = await api.post<{ data: AgentThreadListItem }>("/api/agent/threads", { brand_id: brandId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-threads", brandId] });
    },
  });

  const deleteThread = useMutation({
    mutationFn: (threadId: string) => api.delete<{ data: unknown }>(`/api/agent/threads/${threadId}`),
    onMutate: async (threadId: string) => {
      await queryClient.cancelQueries({ queryKey: ["agent-threads", brandId] });
      const previousThreads = queryClient.getQueryData<{ data: AgentThreadListItem[] }>(["agent-threads", brandId]);
      queryClient.setQueryData<{ data: AgentThreadListItem[] }>(["agent-threads", brandId], (current) => ({
        data: (current?.data ?? []).filter((thread) => thread.id !== threadId),
      }));
      return { previousThreads };
    },
    onError: (_error, _threadId, context) => {
      if (context?.previousThreads) {
        queryClient.setQueryData(["agent-threads", brandId], context.previousThreads);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-threads", brandId] });
    },
  });

  return {
    threads: data?.data ?? [],
    latestThread: data?.data?.[0] ?? null,
    isLoading,
    status,
    refetch,
    createThread: createThread.mutateAsync,
    deleteThread: deleteThread.mutateAsync,
    archivingThreadId: deleteThread.isPending ? deleteThread.variables ?? null : null,
  };
}
